import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FileText, CheckCircle2, IndianRupee, ArrowRight, Loader2 } from "lucide-react";
import { ADMISSION_STEPS, REQUIRED_DOCS, ALL_CLASSES, IMAGES } from "@/lib/constants";
import { PageBanner } from "@/components/PageBanner";
import { SectionHeading } from "@/components/SectionHeading";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/utils2";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FEES = [
  { stage: "Montessori (Pre-Nursery – Sr. KG)", admission: "₹5,000", monthly: "₹2,500" },
  { stage: "Primary (Class 1 – 4)", admission: "₹5,000", monthly: "₹3,200" },
  { stage: "Secondary (Class 5 – 10)", admission: "₹5,000", monthly: "₹4,000" },
];

export default function Admission() {
  const [form, setForm] = useState({
    student_name: "", dob: "", gender: "", applying_class: "", parent_name: "",
    phone: "", email: "", address: "", previous_school: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.student_name || !form.parent_name || !form.phone || !form.applying_class) {
      toast.error("Please fill all required (*) fields.");
      return;
    }
    setLoading(true);
    try {
      await api.createAdmission(form);
      toast.success("Application submitted! Our admission team will contact you soon.");
      setForm({ student_name: "", dob: "", gender: "", applying_class: "", parent_name: "", phone: "", email: "", address: "", previous_school: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageBanner title="Admission" subtitle="Admissions open for session 2026-27. Apply online in a few simple steps." image={IMAGES.students} />

      {/* Process */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <SectionHeading center label="How to Apply" title="Admission Process" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {ADMISSION_STEPS.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="relative bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
              <span className="font-head font-black text-5xl text-slate-100 absolute top-3 right-4">{i + 1}</span>
              <div className="h-11 w-11 rounded-xl bg-brandred/10 text-brandred grid place-items-center mb-4 font-bold">{i + 1}</div>
              <h4 className="font-head font-bold text-navy">{s.title}</h4>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Docs + Fees */}
      <section className="bg-[#F3F4F6] py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm">
            <h3 className="font-head font-bold text-2xl text-navy flex items-center gap-2 mb-5"><FileText className="h-6 w-6 text-brandred" /> Required Documents</h3>
            <ul className="space-y-3">
              {REQUIRED_DOCS.map((d) => (
                <li key={d} className="flex gap-2.5 text-sm text-slate-700"><CheckCircle2 className="h-5 w-5 text-brandteal shrink-0" /> {d}</li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm">
            <h3 className="font-head font-bold text-2xl text-navy flex items-center gap-2 mb-5"><IndianRupee className="h-6 w-6 text-brandred" /> Fee Structure</h3>
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-navy text-white">
                  <tr><th className="text-left px-4 py-3 font-semibold">Stage</th><th className="px-4 py-3 font-semibold">Admission</th><th className="px-4 py-3 font-semibold">Monthly</th></tr>
                </thead>
                <tbody>
                  {FEES.map((f, i) => (
                    <tr key={f.stage} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                      <td className="px-4 py-3 text-slate-700 font-medium">{f.stage}</td>
                      <td className="px-4 py-3 text-center text-slate-600">{f.admission}</td>
                      <td className="px-4 py-3 text-center font-semibold text-navy">{f.monthly}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-3">* Indicative fees. Please contact the office for the exact fee schedule.</p>
          </div>
        </div>
      </section>

      {/* Online form */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <SectionHeading center label="Online Admission" title="Online Admission Form" />
        <form onSubmit={submit} data-testid="admission-form" className="bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-sm grid sm:grid-cols-2 gap-4">
          <Input data-testid="adm-student-name" placeholder="Student's Full Name *" value={form.student_name} onChange={(e) => set("student_name", e.target.value)} />
          <Input data-testid="adm-dob" type="date" placeholder="Date of Birth" value={form.dob} onChange={(e) => set("dob", e.target.value)} />
          <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
            <SelectTrigger data-testid="adm-gender"><SelectValue placeholder="Gender" /></SelectTrigger>
            <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
          </Select>
          <Select value={form.applying_class} onValueChange={(v) => set("applying_class", v)}>
            <SelectTrigger data-testid="adm-class"><SelectValue placeholder="Applying for Class *" /></SelectTrigger>
            <SelectContent>{ALL_CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="adm-parent-name" placeholder="Parent / Guardian Name *" value={form.parent_name} onChange={(e) => set("parent_name", e.target.value)} />
          <Input data-testid="adm-phone" placeholder="Phone Number *" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          <Input data-testid="adm-email" type="email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} />
          <Input data-testid="adm-prev-school" placeholder="Previous School (if any)" value={form.previous_school} onChange={(e) => set("previous_school", e.target.value)} />
          <Textarea data-testid="adm-address" className="sm:col-span-2" placeholder="Residential Address" rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} />
          <Textarea data-testid="adm-message" className="sm:col-span-2" placeholder="Any message (optional)" rows={2} value={form.message} onChange={(e) => set("message", e.target.value)} />
          <Button type="submit" disabled={loading} data-testid="adm-submit" className="sm:col-span-2 bg-brandred hover:bg-brandred/90 text-white rounded-full h-12 font-semibold">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit Application <ArrowRight className="ml-1.5 h-4 w-4" /></>}
          </Button>
        </form>
      </section>
    </div>
  );
}
